/**
 * As partes de tool do turno, por `toolCallId` (spec-001 `plataforma-por-chat`, [T006]).
 *
 * Cada chamada de tool vira UMA parte na tela — o esqueleto enquanto a tool roda,
 * o cartão quando ela responde. O que a parte guarda é o que o registry precisa
 * para escolher o componente (`ui`), o que o cartão precisa para desenhar
 * (`value`) e o que a confirmação precisa para commitar (`preview_id`):
 *
 *   { toolCallId, tool, label, executor, input, state, value, ui, preview_id, modo }
 *
 * ★ O estado é uma MÁQUINA, não um campo livre. O stream emite eventos que podem
 * chegar fora de ordem (reconnect é replay, ver §14 do CLAUDE.md do módulo), e um
 * `tool-approval-request` atrasado que caísse sobre uma parte já resolvida
 * reabriria "confirmar?" sobre uma escrita que JÁ aconteceu. Por isso a seta só
 * anda para frente: uma transição inválida é ignorada com `console.warn` e o
 * estado fica onde está.
 *
 *   input-available ──▶ approval-requested ──▶ output-available
 *          │                     │        └──▶ output-denied
 *          │                     └───────────▶ output-error
 *          └────────────────────────────────▶ output-{available,denied,error}
 *
 * ★ O state nasce de FUNÇÃO (`criarStateToolParts`), nunca de objeto literal
 * compartilhado. O mesmo slice é composto em quatro namespaces
 * (`aawz-intelligence-v2`, `feeFixoIa`, `aiFramework`, `comissoesIa`) e um mapa
 * compartilhado faria o drawer do fee-fixo mostrar os cartões do chat do CRM — o
 * modo de falha já medido em 2026-08-02 com os slices do módulo `fee-fixo`.
 */

// Os cinco estados do contrato (`contracts/events.json` → definitions.ToolState),
// nomeados UMA vez: parser, registries e cartões importam daqui em vez de
// redigitar a string — uma mudança no contrato se faz num lugar só.
// A ordem das chaves é a do ciclo de vida — o teste a trava para que o front e
// o schema do backend continuem falando do mesmo vocabulário.
export const TOOL_PART_STATE = Object.freeze({
  ENTRADA: 'input-available',
  APROVACAO: 'approval-requested',
  SAIDA: 'output-available',
  RECUSA: 'output-denied',
  ERRO: 'output-error',
})

export const ESTADOS_TOOL_PART = Object.values(TOOL_PART_STATE)

export const ESTADO_INICIAL_TOOL_PART = TOOL_PART_STATE.ENTRADA

// Destinos legítimos de cada estado. Os três `output-*` são TERMINAIS: lista
// vazia é a regra, não um esquecimento.
const TRANSICOES_VALIDAS = {
  [TOOL_PART_STATE.ENTRADA]: [
    TOOL_PART_STATE.APROVACAO,
    TOOL_PART_STATE.SAIDA,
    TOOL_PART_STATE.RECUSA,
    TOOL_PART_STATE.ERRO,
  ],
  [TOOL_PART_STATE.APROVACAO]: [TOOL_PART_STATE.SAIDA, TOOL_PART_STATE.RECUSA, TOOL_PART_STATE.ERRO],
  [TOOL_PART_STATE.SAIDA]: [],
  [TOOL_PART_STATE.RECUSA]: [],
  [TOOL_PART_STATE.ERRO]: [],
}

function avisar(motivo, contexto) {
  // Prefixo estável: é por ele que a suíte declara `global.logsEsperados` quando
  // o caminho sob teste é justamente o do aviso (setup.js transforma warn em erro).
  console.warn('[toolParts] ' + motivo, contexto)
}

export function estadoConhecido(estado) {
  return ESTADOS_TOOL_PART.indexOf(estado) !== -1
}

export function transicaoValida(de, para) {
  if (!estadoConhecido(para)) return false
  const destinos = TRANSICOES_VALIDAS[de]
  if (!destinos) return false
  return destinos.indexOf(para) !== -1
}

export function criarStateToolParts() {
  return {
    // { [toolCallId]: parte } — substituído inteiro a cada escrita (Vue 2 não
    // observa chave nova em objeto reativo sem `$set`).
    toolParts: {},
    // Os ids na ordem em que o turno os produziu. O mapa não guarda ordem, e a
    // tela desenha os cartões na sequência em que a IA os chamou.
    toolPartsOrdem: [],
  }
}

// Estado de uma parte NOVA: ausente vira o inicial; desconhecido vira o inicial
// com aviso. Um evento de desfecho que chegue antes do de entrada (o
// `tool-input-available` se perdeu) cria a parte já resolvida — recusar aqui
// deixaria o cartão fora da tela para sempre.
function estadoDeCriacao(estado, toolCallId) {
  if (!estado) return ESTADO_INICIAL_TOOL_PART
  if (!estadoConhecido(estado)) {
    avisar('estado desconhecido na criação', { toolCallId: toolCallId, state: estado })
    return ESTADO_INICIAL_TOOL_PART
  }
  return estado
}

// Estado de uma parte EXISTENTE. Repetição do mesmo estado é silenciosa (o
// dual-emit reemite o desfecho); só a seta para trás — ou para lugar nenhum — avisa.
function estadoDeMerge(estadoAtual, estadoProposto, toolCallId) {
  if (!estadoProposto || estadoProposto === estadoAtual) return estadoAtual
  if (transicaoValida(estadoAtual, estadoProposto)) return estadoProposto
  avisar('transição inválida', { toolCallId: toolCallId, de: estadoAtual, para: estadoProposto })
  return estadoAtual
}

export const toolPartsMutations = {
  UPSERT_TOOL_PART(state, parte) {
    if (!parte || !parte.toolCallId) {
      avisar('parte sem toolCallId ignorada', { parte: parte })
      return
    }

    const id = String(parte.toolCallId)
    const atual = state.toolParts[id]

    if (!atual) {
      const nova = { ...parte, toolCallId: id, state: estadoDeCriacao(parte.state, id) }
      state.toolParts = { ...state.toolParts, [id]: nova }
      state.toolPartsOrdem = state.toolPartsOrdem.concat([id])
      return
    }

    // Os campos SEMPRE mesclam — um evento recusado pela máquina de estados
    // ainda pode trazer `value`/`ui`/`preview_id` que a tela precisa. O que a
    // guarda protege é o `state`, não o payload.
    const mesclada = { ...atual, ...parte, toolCallId: id }
    mesclada.state = estadoDeMerge(atual.state, parte.state, id)
    state.toolParts = { ...state.toolParts, [id]: mesclada }
  },

  SET_TOOL_PART_STATE(state, payload) {
    if (!payload || !payload.toolCallId) {
      avisar('setToolPartState sem toolCallId', { payload: payload })
      return
    }

    const id = String(payload.toolCallId)
    const atual = state.toolParts[id]

    if (!atual) {
      avisar('estado de parte desconhecida ignorado', { toolCallId: id, state: payload.state })
      return
    }

    if (payload.state === atual.state) return

    if (!transicaoValida(atual.state, payload.state)) {
      avisar('transição inválida', { toolCallId: id, de: atual.state, para: payload.state })
      return
    }

    state.toolParts = { ...state.toolParts, [id]: { ...atual, state: payload.state } }
  },

  RESET_TOOL_PARTS(state) {
    state.toolParts = {}
    state.toolPartsOrdem = []
  },
}

export const toolPartsGetters = {
  toolParts: (state) => state.toolParts || {},

  toolPartsOrdenadas: (state) => {
    const mapa = state.toolParts || {}
    return (state.toolPartsOrdem || []).map((id) => mapa[id]).filter((parte) => Boolean(parte))
  },

  // Devolve `null`, nunca `undefined` — a convenção dos getters deste módulo (§9).
  toolPartPorId: (state) => (toolCallId) => {
    const mapa = state.toolParts || {}
    const parte = mapa[String(toolCallId)]
    return parte || null
  },
}

// Actions são setters puros — o stream mora nas classes de API (§9 do CLAUDE.md).
export const toolPartsActions = {
  upsertToolPart({ commit }, parte) {
    commit('UPSERT_TOOL_PART', parte)
  },

  setToolPartState({ commit }, payload) {
    commit('SET_TOOL_PART_STATE', payload)
  },

  // Chamado no início de cada turno: as partes são do TURNO, não da conversa —
  // o que sobrevive ao reload é `tool_calls[]` na mensagem persistida.
  clearToolParts({ commit }) {
    commit('RESET_TOOL_PARTS')
  },
}

// Módulo autocontido, para quem preferir registrar o slice namespaced
// (`<ns>/toolParts/*`) em vez de compor no namespace raiz. `state` é função:
// registrar o MESMO objeto em dois stores continua dando dois estados.
export default {
  namespaced: true,
  state: criarStateToolParts,
  getters: toolPartsGetters,
  mutations: toolPartsMutations,
  actions: toolPartsActions,
}
